/**
 * Client-side matrix pivot (mirrors backend matrixReportPivot.js).
 */

import {
  DEFAULT_DATE_GROUP_GRANULARITY,
  getGroupBucketFromRow,
  applyDateGranularityToGroups,
} from './matrixDateGrouping';
import { getMeasurePivotValue, formatMatrixMeasureCell } from './matrixCurrencyFormat';
import { sortMatrixAxisKeys } from '../charts/chartPicklistColors';

const DEFAULT_COL_KEY = '__all__';

function makeCompositeGroupKey(fields, row, dateOptions) {
  if (!fields?.length) {
    return { key: '', parts: [], label: '' };
  }
  const buckets = fields.map(f => getGroupBucketFromRow(row, f, dateOptions));
  return {
    key: buckets.map(b => b.key).join('|'),
    parts: buckets.map(b => b.label),
    label: buckets.map(b => b.label).join(' / '),
  };
}

function splitCompositeKey(key, fieldCount) {
  if (!key) return [];
  const parts = String(key).split('|');
  if (fieldCount <= 1) return [parts[0] ?? ''];
  return parts;
}

function parseNumeric(value) {
  if (value == null) return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object' && value !== null) {
    if (value.value !== undefined) return parseNumeric(value.value);
    if (value.displayValue !== undefined) return parseNumeric(value.displayValue);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t === 'N/A') return NaN;
    const cleaned = t.replace(/[₹$€£,\s]/g, '').replace(/%$/, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

export function aggregateValues(values, aggregation) {
  const agg = (aggregation || 'sum').toLowerCase();
  if (agg === 'count') return values.length;
  const nums = values.map(parseNumeric).filter(Number.isFinite);
  if (!nums.length) return 0;
  switch (agg) {
    case 'avg':
    case 'average':
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    case 'sum':
    default:
      return nums.reduce((a, b) => a + b, 0);
  }
}

/** Resolve row total — use pivot bucket totals, else sum aggregated column cells. */
export function resolveRowTotalValue(grid, rowKey, measure) {
  const mid = measure.id || measure.field_name;
  const direct = grid.rowTotalCells?.[rowKey]?.[mid];
  if (direct != null && Number.isFinite(direct)) return direct;

  const colKeys = grid.columnKeys || [];
  const cellValues = colKeys
    .map(ck => grid.cells?.[rowKey]?.[ck]?.[mid])
    .filter(v => v != null && Number.isFinite(v));

  if (!cellValues.length) return direct ?? 0;
  return aggregateValues(cellValues, measure.aggregation || 'sum');
}

/** @deprecated use formatMatrixMeasureCell */
export function formatMatrixCellValue(value, aggregation, measure, currencyOptions) {
  return formatMatrixMeasureCell(value, measure || {}, aggregation, currencyOptions);
}

export function buildMatrixGrid(
  rows,
  rowGroups,
  columnGroups,
  measures,
  dateOptions = {}
) {
  const matrixDateGranularity =
    dateOptions.matrixDateGranularity || DEFAULT_DATE_GROUP_GRANULARITY;
  const dateGranularityByFieldId = dateOptions.dateGranularityByFieldId || {};
  const pivotDateOptions = { matrixDateGranularity, dateGranularityByFieldId };
  const currencyOptions = {
    baseCurrencyCode: dateOptions.baseCurrencyCode || null,
    exchangeRateLookup: dateOptions.exchangeRateLookup || null,
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const rowGroupFields = applyDateGranularityToGroups(
    rowGroups,
    dateGranularityByFieldId,
    matrixDateGranularity
  );
  const columnGroupFields = applyDateGranularityToGroups(
    columnGroups,
    dateGranularityByFieldId,
    matrixDateGranularity
  );
  const measureList =
    Array.isArray(measures) && measures.length > 0
      ? measures
      : [{ id: '__count__', field_name: '__count__', field_label: 'Count', aggregation: 'count' }];

  const buckets = {};
  const rowKeysSet = new Set();
  const colKeysSet = new Set();
  const rowKeyMeta = {};
  const colKeyMeta = {};

  for (const row of safeRows) {
    const rowComposite = rowGroupFields.length
      ? makeCompositeGroupKey(rowGroupFields, row, pivotDateOptions)
      : { key: '__all__', parts: ['All'], label: 'All' };
    const colComposite = columnGroupFields.length
      ? makeCompositeGroupKey(columnGroupFields, row, pivotDateOptions)
      : { key: DEFAULT_COL_KEY, parts: ['Total'], label: 'Total' };

    const rowKey = rowComposite.key;
    const colKey = colComposite.key;

    rowKeysSet.add(rowKey);
    colKeysSet.add(colKey);
    if (!rowKeyMeta[rowKey]) rowKeyMeta[rowKey] = rowComposite;
    if (!colKeyMeta[colKey]) colKeyMeta[colKey] = colComposite;

    if (!buckets[rowKey]) buckets[rowKey] = {};
    if (!buckets[rowKey][colKey]) buckets[rowKey][colKey] = {};

    for (const measure of measureList) {
      const mid = measure.id || measure.field_name;
      if (!buckets[rowKey][colKey][mid]) buckets[rowKey][colKey][mid] = [];

      const pivotVal = getMeasurePivotValue(row, measure, currencyOptions);
      if (pivotVal != null) {
        buckets[rowKey][colKey][mid].push(pivotVal);
      }
    }
  }

  const reportFields = dateOptions.reportFields || [];
  const picklistValuesByFieldId = dateOptions.picklistValuesByFieldId || null;
  const rowKeys = sortMatrixAxisKeys(
    rowKeysSet,
    rowGroupFields,
    reportFields,
    rowKeyMeta,
    picklistValuesByFieldId
  );
  const columnKeys = sortMatrixAxisKeys(
    colKeysSet,
    columnGroupFields,
    reportFields,
    colKeyMeta,
    picklistValuesByFieldId
  );

  const cells = {};
  const rowTotalCells = {};
  const columnTotalCells = {};
  const grandTotalCells = {};
  const rowTotals = {};
  const columnTotals = {};
  const grandTotals = {};

  for (const rk of rowKeys) {
    cells[rk] = {};
    rowTotals[rk] = {};
    for (const ck of columnKeys) {
      cells[rk][ck] = {};
      for (const measure of measureList) {
        const mid = measure.id || measure.field_name;
        const vals = buckets[rk]?.[ck]?.[mid] || [];
        const agg = measure.aggregation || 'sum';
        const value = aggregateValues(vals, agg);
        cells[rk][ck][mid] = value;

        if (!rowTotals[rk][mid]) rowTotals[rk][mid] = [];
        rowTotals[rk][mid].push(...vals);

        if (!columnTotals[ck]) columnTotals[ck] = {};
        if (!columnTotals[ck][mid]) columnTotals[ck][mid] = [];
        columnTotals[ck][mid].push(...vals);

        if (!grandTotals[mid]) grandTotals[mid] = [];
        grandTotals[mid].push(...vals);
      }
    }
  }

  for (const rk of rowKeys) {
    rowTotalCells[rk] = {};
    for (const measure of measureList) {
      const mid = measure.id || measure.field_name;
      rowTotalCells[rk][mid] = aggregateValues(rowTotals[rk][mid] || [], measure.aggregation);
    }
  }

  for (const ck of columnKeys) {
    columnTotalCells[ck] = {};
    for (const measure of measureList) {
      const mid = measure.id || measure.field_name;
      columnTotalCells[ck][mid] = aggregateValues(columnTotals[ck][mid] || [], measure.aggregation);
    }
  }

  for (const measure of measureList) {
    const mid = measure.id || measure.field_name;
    grandTotalCells[mid] = aggregateValues(grandTotals[mid] || [], measure.aggregation);
  }

  const rowLabels = rowKeys.map(key => {
    const meta = rowKeyMeta[key];
    return {
      key,
      parts: meta?.parts?.length
        ? meta.parts
        : splitCompositeKey(key === '__all__' ? '' : key, rowGroupFields.length),
      label: meta?.label || key,
    };
  });

  const columnLabels = columnKeys.map(key => {
    const meta = colKeyMeta[key];
    return {
      key,
      parts: meta?.parts?.length
        ? meta.parts
        : splitCompositeKey(key === DEFAULT_COL_KEY ? '' : key, columnGroupFields.length),
      label: meta?.label || (key === DEFAULT_COL_KEY ? 'Total' : key),
    };
  });

  return {
    rowKeys,
    columnKeys,
    rowLabels,
    columnLabels,
    rowGroupFields,
    columnGroupFields,
    measures: measureList,
    cells,
    rowTotalCells,
    columnTotalCells,
    grandTotalCells,
    currencyOptions,
    formatMatrixMeasureCell,
  };
}
